import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from '../../services/theme.service';
import { MenuService } from '../../services/menu.service';
import { StorageService } from '../../services/storage.service';
import { EncryptionService } from '../../services/encryption.service';
import { TenantService } from '../../services/tenant.service';
import { DecryptedUserPermissions, DecryptedOtherSettings } from '../../models/user-permissions.model';
import { Subscription, catchError, EMPTY } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IonIcon } from "@ionic/angular/standalone";

@Component({
  selector: 'app-sidemenu',
  imports: [
    CommonModule,
    IonIcon
  ],
  templateUrl: './sidemenu.component.html',
  styleUrls: ['./sidemenu.component.scss']
})
export class SidemenuComponent implements OnInit, OnDestroy {
  isCollapsed = false;
  activeMenu: string | null = null; // Single state variable for active menu
  isDarkMode = false;
  currentRoute = '';
  currentUrl = '';
  logoUrl: string | null = null;
  tenantName: string | null = null;
  userPermissions: DecryptedUserPermissions | null = null;
  /** Pay Bills redirect URL from othersetting (pay-bills-redirect_other) when enabled; null otherwise. */
  payBillsRedirectUrl: string | null = null;
  /** True when viewport is desktop (min-width: 1025px). Logo/practice name shown in header on desktop, in sidemenu on mobile. */
  isDesktop = window.matchMedia('(min-width: 1025px)').matches;
  private breakpoint = window.matchMedia('(min-width: 1025px)');
  private breakpointChangeListener?: () => void;
  private subscription: Subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private themeService: ThemeService,
    private menuService: MenuService,
    private storageService: StorageService,
    private encryptionService: EncryptionService,
    private tenantService: TenantService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // QP-FE-ML-002 — keep isDesktop in sync on viewport resize
    this.breakpointChangeListener = () => {
      this.isDesktop = this.breakpoint.matches;
    };
    this.breakpoint.addEventListener('change', this.breakpointChangeListener);

    // Get user permissions from session storage
    const storedPermissions = this.storageService.getSessionStorage('user_permissions');
    if (storedPermissions) {
      this.userPermissions = storedPermissions as DecryptedUserPermissions;
    }

    // Resolve Pay Bills redirect URL from other settings (pay-bills-redirect_other)
    this.payBillsRedirectUrl = this.getPayBillsRedirectUrl();
    
    // Initialize menu state - on mobile, start with menu closed
    if (window.innerWidth <= 1024) {
      this.isCollapsed = true;
      this.menuService.setIsCollapsed(true);
    } else {
      // Get initial collapsed state for desktop
      this.isCollapsed = this.menuService.getIsCollapsed();
    }

    // Subscribe to collapsed state changes
    this.subscription.add(
      this.menuService.isCollapsed$.subscribe(isCollapsed => {
        this.isCollapsed = isCollapsed;
      })
    );

    // Subscribe to theme changes
    this.subscription.add(
      this.themeService.isDarkMode().subscribe(isDark => {
        this.isDarkMode = isDark;
      })
    );

    // Get initial theme value
    this.isDarkMode = this.themeService.getCurrentValue();

    // Track current route
    this.updateActiveRoute();

    // Auto-expand send-message-menu-item if on submenu routes
    this.checkAndExpandSubmenu();

    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.updateActiveRoute();
          this.checkAndExpandSubmenu();
        })
    );

    // Fetch logo URL from API
    this.fetchLogoUrl();
    
    // Get tenant name for fallback
    this.loadTenantName();
  }

  loadTenantName(): void {
    // Try to get tenant from TenantService first
    const currentTenant = this.tenantService.getCurrentTenant();
    if (currentTenant?.tenantName) {
      this.tenantName = currentTenant.tenantName;
      return;
    }

    // If not available, try to load by tenant code from storage
    const tenantCode = this.storageService.getSessionStorage('tenant_code');
    if (tenantCode) {
      this.subscription.add(
        this.tenantService.getTenantByCode(tenantCode).subscribe(tenant => {
          if (tenant?.tenantName) {
            this.tenantName = tenant.tenantName;
          }
        })
      );
    }
  }

  // PE-6337 — async AES-GCM encrypt for logo tenant code
  async fetchLogoUrl(): Promise<void> {
    // Get tenant code from sessionStorage (already decrypted by getSessionStorage)
    const tenantCode = this.storageService.getSessionStorage('tenant_code');

    if (!tenantCode) {
      // If no tenant code, don't set logoUrl (will show tenant name instead)
      return;
    }

    // Encrypt tenant code using AES-256-GCM before passing to API
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);

    // AuthService caches logo URL in memory (same as getTemplateMessageById) - no session storage
    this.subscription.add(
      this.authService.getLogoSignUrl(encryptedTenantCode)
        .pipe(
          catchError((error: any) => {
            console.error('Error fetching logo URL:', error);
            // Don't set logoUrl on error - will show tenant name instead
            this.logoUrl = null;
            return EMPTY;
          })
        )
        .subscribe((response: any) => {
          if (response && response.signurl) {
            this.logoUrl = response.signurl;
          } else {
            // Don't set logoUrl if signurl is not available - will show tenant name instead
            this.logoUrl = null;
          }
        })
    );
  }

  // Handle logo loading errors
  onLogoError(event: any): void {
    // Hide the image if it fails to load (will show tenant name instead)
    this.logoUrl = null;
    event.target.style.display = 'none';
  }

  ngOnDestroy(): void {
    if (this.breakpointChangeListener) {
      this.breakpoint.removeEventListener('change', this.breakpointChangeListener);
      this.breakpointChangeListener = undefined;
    }
    this.subscription.unsubscribe();
  }

  private updateActiveRoute(): void {
    this.currentUrl = this.router.url;
    this.currentRoute = this.currentUrl.split('?')[0];
  }

  isActiveRoute(route: string): boolean {
    // Exact match or route starts with the given route path
    const isActive = this.currentRoute === route || this.currentRoute.startsWith(route + '/');
    return isActive;
  }

  toggleSubmenu(menuKey: string): void {
    // When collapsed, navigate to the first submenu item
    if (this.isCollapsed && menuKey === 'chart') {
      this.navigateTo('/health-record');
      return;
    }
    if (this.isCollapsed && menuKey === 'statementsPayment') {
      this.navigateTo('/statements');
      return;
    }

    if (this.isCollapsed) {
      return;
    }

    if (menuKey === 'statementsPayment') {
      this.activeMenu = this.activeMenu === menuKey ? null : menuKey;
      if (this.activeMenu === menuKey) {
        this.navigateTo('/statements');
      }
      return;
    }

    // Toggle: if already active, close it; otherwise, set it as active
    this.activeMenu = this.activeMenu === menuKey ? null : menuKey;
  }

  setActiveMenu(menuKey: string | null): void {
    this.activeMenu = menuKey;
  }

  toggleDarkMode(): void {
    this.themeService.toggleDarkMode();
  }

  get isMenuOpen(): boolean {
    return !this.isCollapsed;
  }

  toggleMenu(): void {
    this.menuService.toggleSideMenu();
  }

  logout(): void {
    this.authService.logout();
  }

  /**
   * Get redirect URL for Pay Bills from other settings when setting "pay-bills-redirect_other"
   * is present and enabled (settingvalue === 'true'). Returns jsondata.url or null.
   */
  private getPayBillsRedirectUrl(): string | null {
    const otherSettings = this.storageService.getSessionStorage('user_other_settings') as DecryptedOtherSettings | null;
    if (!otherSettings || !Array.isArray(otherSettings)) {
      return null;
    }
    const setting = otherSettings.find(
      (s) =>
        s.settingname?.toLowerCase().trim() === 'pay-bills-redirect_other' &&
        s.settingvalue?.toLowerCase().trim() === 'true'
    );
    const url = setting?.jsondata?.url;
    return typeof url === 'string' && url ? url : null;
  }

  /**
   * Handle Pay Bills menu click: redirect to external URL when pay-bills-redirect_other
   * is enabled with a url in jsondata; otherwise navigate to /pay-bills as before.
   */
  onPayBillsClick(): void {
    if (this.payBillsRedirectUrl) {
      window.location.href = this.payBillsRedirectUrl;
      return;
    }
    this.navigateTo('/pay-bills');
  }

  navigateTo(route: string): void {
    // Determine menu key based on route
    let menuKey: string | null = null;

    // Route mapping to menu keys
    if (route === '/home' || route.startsWith('/home')) {
      menuKey = 'home';
    } else if (route === '/messages' || route.startsWith('/messages')) {
      menuKey = 'myMessages';
    } else if (route === '/ask-provider' || route.startsWith('/ask-provider')) {
      menuKey = 'sendMessage';
    } else if (route === '/appointments' || route.startsWith('/appointments')) {
      menuKey = 'appointments';
    } else if (route === '/health-record' ||
      route.startsWith('/health-record') ||
      route === '/phi/view' ||
      route === '/phi/submit' ||
      route.startsWith('/phi')) {
      menuKey = 'chart';
    } else if (route === '/patient-forms' || route.startsWith('/patient-forms')) {
      menuKey = 'patientForms';
    } else if (route === '/account-on-file' || route.startsWith('/account-on-file')) {
      menuKey = 'accountOnFile';
    } else if (route === '/pay-bills' || route.startsWith('/pay-bills') ||
      route === '/statements' || route.startsWith('/statements')) {
      menuKey = 'statementsPayment';
    } else if (route === '/profile' || route.startsWith('/profile')) {
      menuKey = 'profile';
    } else if (route === '/audit-log' || route.startsWith('/audit-log')) {
      menuKey = 'myAccessLog';
    } else if (route === '/restriction' || route.startsWith('/restriction')) {
      menuKey = 'restrictionRequest';
    } else if (route === '/patientMsg' || route.startsWith('/patientMsg')) {
      menuKey = 'patientMessage';
    }

    // Set active menu (will be updated by checkAndExpandSubmenu after navigation)
    this.activeMenu = menuKey;

    // Close menu on mobile after navigation
    this.closeMenuOnMobile();

    // Navigate and update route immediately for proper active state
    this.router.navigate([route.split('?')[0]], { queryParams: this.getQueryParams(route) }).then(() => {
      this.updateActiveRoute();
      this.checkAndExpandSubmenu();
    });
  }

  /**
   * Close menu on mobile devices
   */
  closeMenuOnMobile(): void {
    // Check if we're on mobile viewport
    if (window.innerWidth <= 1024 && !this.isCollapsed) {
      // Close menu by setting it to collapsed
      this.menuService.setIsCollapsed(true);
    }
  }

  private getQueryParams(route: string): any {
    const queryString = route.split('?')[1];
    if (!queryString) return {};

    const params: any = {};
    queryString.split('&').forEach(param => {
      const [key, value] = param.split('=');
      if (key) params[key] = value || true;
    });
    return params;
  }

  private checkAndExpandSubmenu(): void {
    // Determine which menu should be active based on current route
    let menuKey: string | null = null;

    // Route mapping to menu keys
    if (this.currentRoute === '/home' || this.currentRoute.startsWith('/home')) {
      menuKey = 'home';
    } else if (this.currentRoute === '/messages' || this.currentRoute.startsWith('/messages')) {
      menuKey = 'myMessages';
    } else if (this.currentRoute === '/ask-provider' || this.currentRoute.startsWith('/ask-provider')) {
      menuKey = 'sendMessage';
    } else if (this.currentRoute === '/appointments' || this.currentRoute.startsWith('/appointments')) {
      menuKey = 'appointments';
    } else if (this.currentRoute === '/health-record' ||
      this.currentRoute.startsWith('/health-record') ||
      this.currentRoute === '/phi/view' ||
      this.currentRoute === '/phi/submit' ||
      this.currentRoute.startsWith('/phi')) {
      menuKey = 'chart';
    } else if (this.currentRoute === '/patient-forms' || this.currentRoute.startsWith('/patient-forms')) {
      menuKey = 'patientForms';
    }else if (this.currentRoute === '/account-on-file' || this.currentRoute.startsWith('/account-on-file')) {
      menuKey = 'accountOnFile';
    }else if (this.currentRoute === '/pay-bills' || this.currentRoute.startsWith('/pay-bills') ||
      this.currentRoute === '/statements' || this.currentRoute.startsWith('/statements')) {
      menuKey = 'statementsPayment';
    } else if (this.currentRoute === '/profile' || this.currentRoute.startsWith('/profile')) {
      menuKey = 'profile';
    } else if (this.currentRoute === '/audit-log' || this.currentRoute.startsWith('/audit-log')) {
      menuKey = 'myAccessLog';
    } else if (this.currentRoute === '/restriction' || this.currentRoute.startsWith('/restriction')) {
      menuKey = 'restrictionRequest';
    } else if (this.currentRoute === '/patientMsg' || this.currentRoute.startsWith('/patientMsg')) {
      menuKey = 'patientMessage';
    }

    // Set active menu
    this.activeMenu = menuKey;
  }
  
  /**
   * Check if a permission is enabled
   */
  hasPermission(permission: keyof DecryptedUserPermissions): boolean {
    if (!this.userPermissions) {
      return true; // Default to showing if permissions not loaded
    }
    return this.userPermissions[permission] === true;
  }
  
  /**
   * Check if any message-related permission is enabled
   * If all message permissions are false, hide messages menu item
   */
  hasAnyMessagePermission(): boolean {
    if (!this.userPermissions) {
      return true; // Default to showing if permissions not loaded
    }
    return (
      this.userPermissions.askprovider === true ||
      this.userPermissions.apptrequest === true ||
      this.userPermissions.askstaff === true ||
      this.userPermissions.inbox === true ||
      this.userPermissions.sent === true ||
      this.userPermissions.healthinformationsharehistory === true
    );
  }

  /**
   * Check if user has selected an associated patient
   * This is indicated by the presence of patientCode or patientEmail in user_info
   */
  hasAssociatedPatientSelected(): boolean {
    const userInfo = this.storageService.getSessionStorage('user_info');
    if (!userInfo) {
      return false;
    }
    // Check if patientCode or patientEmail exists (these are set when a patient is selected)
    return !!(userInfo.patientCode || userInfo.patientEmail);
  }
}
